import type { CSSProperties, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Anchor, Badge, Center, Drawer, Group, Loader, Stack, Table, Text } from '@mantine/core'
import { IconAlertCircle, IconInfoCircle, IconPlugConnectedX, IconSearch, IconUsers } from '@tabler/icons-react'
import { EmptyState, HairlineDivider, MetaList, SectionTitle } from '@lighthouse/design'
import { RestrictedState } from './RestrictedState'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { useReportAccess } from '../../hooks/useReportAccess'
import { isApiError } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, whenUtcLabel } from '../../utils/format'
import type {
  ReportAccess,
  ReportAccessGrant,
  ReportAccessMember,
  ReportAccessOwnership,
  ReportAccessPath,
  ReportAccessPrincipal,
  ReportAccessReport,
  ReportAccessResponsibility,
} from '../../types/reportAccess'

/** What RestrictedState names when the caller lacks Lighthouse.Admin. */
const SURFACE = 'The report access explanation'

/**
 * Why each person who can reach one report can reach it.
 *
 * Opened from the Report Registry and the Governance Queue with a report id;
 * `null` closes it. Everything it says is what the server resolved — the server
 * mirrors Cartographer's gate precedence and a unit test pins it against the gate
 * — so this component derives no access verdict of its own.
 *
 * A drawer rather than a page because the reader is working down a list and the
 * explanation is about one row of it; closing it returns them to their place.
 */
export function ReportAccessDrawer({
  reportId,
  onClose,
}: {
  reportId: string | null
  onClose: () => void
}) {
  return (
    <Drawer
      opened={reportId !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={<Text fw={600}>Who can reach this report, and why</Text>}
    >
      {/* Keyed on the report, so moving to another report remounts the body rather than flashing the last one. */}
      {reportId && <ReportAccessBody key={reportId} reportId={reportId} onClose={onClose} />}
    </Drawer>
  )
}

/**
 * The drawer's contents for one report. Exported for the render tests: Mantine's Drawer portals its
 * children, and a portal renders nothing under react-dom/server.
 */
export function ReportAccessBody({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const gate = useLighthouseAdmin()
  const { data, isLoading, isError, error } = useReportAccess(reportId)

  // Denied renders WITHOUT fetching — the hook is disabled on `denied`.
  if (gate.state === 'denied') {
    return <RestrictedState surface={SURFACE} reason={gate.reason} />
  }

  if (isError) {
    return <AccessError error={error} />
  }

  // `|| !data`: between React Query retries the flags read "loaded, no error" with
  // no data, which must not render as "nobody can reach this report".
  if (isLoading || !data) {
    return <Center py="xl"><Loader /></Center>
  }

  return <AccessExplanation access={data} onClose={onClose} />
}

/** Restricted, missing and broken look different, and none of them reads as "nobody has access". */
function AccessError({ error }: { error: unknown }) {
  const kind = errorKind(error)

  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? error.detail : null} />
  }

  if (kind === 'not-found') {
    return (
      <EmptyState
        icon={<IconSearch size={28} stroke={1.5} />}
        title="No such report"
        message={
          'The server has no report with this id. Every lifecycle status is explained here, so this is not '
          + 'an archived or delisted report being hidden — the id itself matches nothing.'
        }
      />
    )
  }

  if (kind === 'server') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="Could not load access — AddGrantExpiryAndNotes may not be applied"
        message={
          'The API failed while reading this report’s grants and memberships. The likeliest cause outside '
          + 'local dev is migration AddGrantExpiryAndNotes not having been applied: this explanation reads the '
          + 'membership expiry and both grant notes it adds, and migrations here are applied by hand. This says '
          + 'nothing about who can reach the report.'
        }
      />
    )
  }

  if (kind === 'unauthorized') {
    return (
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="Not signed in"
        message="The API did not accept this session's credentials. Sign in again, or check the API key."
      />
    )
  }

  if (kind === 'network') {
    return (
      <EmptyState
        icon={<IconPlugConnectedX size={28} stroke={1.5} />}
        title="Could not reach the API"
        message="The request never got an answer. This says nothing about who can reach the report."
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertCircle size={28} stroke={1.5} />}
      title="Could not load access"
      message={`${errorSummary(error) ?? 'The request failed.'} This says nothing about who can reach the report.`}
    />
  )
}

function AccessExplanation({ access, onClose }: { access: ReportAccess; onClose: () => void }) {
  const navigate = useNavigate()
  const { report } = access

  const go = (path: string) => {
    onClose()
    navigate(path)
  }

  return (
    <Stack gap="lg" pb="xl">
      <ReportHeader report={report} evaluatedAt={access.evaluatedAt} />

      <NotKnowableNote lines={access.notKnowable} />

      <section>
        <SectionTitle subtitle="The path that decides each person's role, in the order Cartographer's gate applies them">
          Effective access
        </SectionTitle>
        <EffectiveAccess principals={access.principals} count={access.principalCount} />
      </section>

      <HairlineDivider />

      <section>
        <SectionTitle subtitle="Structural authority, held as columns on the report rather than as grants">
          Ownership
        </SectionTitle>
        <Ownership ownership={access.ownership} />
      </section>

      <HairlineDivider />

      <section>
        <SectionTitle subtitle="Every explicit grant row, live or expired, of any principal type">
          Direct grants
        </SectionTitle>
        <DirectGrants grants={access.directGrants} count={access.directGrantCount} />
      </section>

      <HairlineDivider />

      <section>
        <SectionTitle subtitle="Membership confers its role on every report filed in the workspace">
          Workspace membership
        </SectionTitle>
        <WorkspaceMembership report={report} members={access.workspaceMembers} count={access.workspaceMemberCount} />
      </section>

      <HairlineDivider />

      <section>
        <SectionTitle subtitle="Ownership and maintainership transfers, newest first">
          Responsibility history
        </SectionTitle>
        <ResponsibilityHistory rows={access.responsibilityHistory} count={access.responsibilityHistoryCount} />
      </section>

      <HairlineDivider />

      <section>
        <SectionTitle subtitle="Revoking a grant and removing a member delete the row, so the audit trail is the only record">
          Removed access
        </SectionTitle>
        <Stack gap={6} mt="xs">
          <Anchor component="button" type="button" size="sm" ta="left" onClick={() => go(`/audit?entityId=${encodeURIComponent(access.auditLinks.reportEntityId)}`)}>
            Removed or changed access for this report →
          </Anchor>
          {access.auditLinks.workspaceEntityId && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="left"
              onClick={() => go(`/audit?entityId=${encodeURIComponent(access.auditLinks.workspaceEntityId!)}`)}
            >
              Membership changes for this workspace →
            </Anchor>
          )}
          <Text size="xs" c="dimmed">
            Grants and transfers are recorded against the report; workspace membership changes against the
            workspace, which is why a filed report has two links.
          </Text>
        </Stack>
      </section>
    </Stack>
  )
}

function ReportHeader({ report, evaluatedAt }: { report: ReportAccessReport; evaluatedAt: string }) {
  return (
    <Stack gap={4}>
      <Group gap="xs" wrap="wrap">
        <Text fw={600} size="lg">{report.name}</Text>
        <Badge variant="light" color="gray">{report.status}</Badge>
      </Group>
      <Text size="sm">
        <Text span c="dimmed">Filed in: </Text>
        {workspaceLabel(report)}
      </Text>
      {!report.isRunnable && (
        <Text size="xs" c="dimmed">
          A {report.status} report is readable history: every role below can view it, and nobody — the owner
          included — can run, edit or schedule it.
        </Text>
      )}
      <Text size="xs" c="dimmed">Evaluated {whenUtcLabel(evaluatedAt)}. Expired rows are judged against that instant.</Text>
    </Stack>
  )
}

function NotKnowableNote({ lines }: { lines: string[] }) {
  return (
    <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title="What this cannot know">
      <Stack gap={4}>
        {lines.map((line) => <Text key={line} size="sm">{line}</Text>)}
      </Stack>
    </Alert>
  )
}

function EffectiveAccess({ principals, count }: { principals: ReportAccessPrincipal[]; count: number }) {
  if (principals.length === 0) {
    return (
      <EmptyState
        icon={<IconUsers size={28} stroke={1.5} />}
        title="No user holds a path to this report"
        message={
          'No owner, maintainer, user grant or workspace membership names anyone. That is a measured answer, '
          + 'not a failure to load — and Cartographer.Admin holders can still reach it.'
        }
      />
    )
  }

  return (
    <Stack gap="xs" mt="xs">
      <Table.ScrollContainer minWidth={560}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Person</Table.Th>
              <Table.Th>Path that decides</Table.Th>
              <Table.Th>Role</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {principals.map((principal) => {
              const noPath = principal.effectivePath === 'None'
              return (
                <Table.Tr key={principal.principalId} style={noPath ? MUTED : undefined}>
                  <Table.Td>
                    <Person name={principal.displayName} id={principal.principalId} />
                    {principal.isWorkspaceOwnerSelfMembership && (
                      <Badge size="xs" variant="outline" color="gray" mt={4}>Workspace owner (own membership)</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{PATH_LABEL[principal.effectivePath] ?? principal.effectivePath}</Text>
                    {noPath && (
                      <Text size="xs" c="dimmed">Listed because a row names them; none of those rows confers access now.</Text>
                    )}
                    {principal.outrankedPaths.map((outranked) => (
                      <Text
                        key={`${outranked.path}-${outranked.role}`}
                        size="xs"
                        c="dimmed"
                        td={outranked.isLive ? undefined : 'line-through'}
                      >
                        {outranked.why}
                      </Text>
                    ))}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={noPath ? undefined : 500}>{noPath ? 'No access' : principal.effectiveRole}</Text>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <ShowingCount shown={principals.length} total={count} noun="people" />
    </Stack>
  )
}

function Ownership({ ownership }: { ownership: ReportAccessOwnership }) {
  return (
    <Stack gap="sm" mt="xs">
      <MetaList
        items={[
          {
            label: 'Business owner (as stored)',
            value: ownership.businessOwnerPrincipalId == null
              ? NOT_MEASURED
              : `${ownership.businessOwnerPrincipalId} · ${ownership.businessOwnerPrincipalType ?? 'User (type not recorded)'}`,
            mono: ownership.businessOwnerPrincipalId != null,
          },
          {
            label: 'Holds the owner path',
            value: ownership.ownerUserId == null
              ? 'Nobody'
              : (
                <>
                  <Person name={ownership.ownerDisplayName} id={ownership.ownerUserId} />
                  {ownership.ownerIsCreatorFallback && (
                    <Text size="xs" c="dimmed">By the creator fallback: no business owner is recorded.</Text>
                  )}
                </>
              ),
          },
          {
            label: 'Maintainer (as stored)',
            value: ownership.maintainerPrincipalId == null
              ? NOT_MEASURED
              : `${ownership.maintainerPrincipalId} · ${ownership.maintainerPrincipalType ?? 'User (type not recorded)'}`,
            mono: ownership.maintainerPrincipalId != null,
          },
          { label: 'Author', value: ownership.authorUserId ?? NOT_MEASURED, mono: ownership.authorUserId != null },
          { label: 'Creator name (stamped at creation)', value: ownership.creatorSnapshot ?? NOT_MEASURED },
        ]}
      />
      {(ownership.ownerConfersToNobody || ownership.ownershipNote) && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="gray"
          variant="light"
          title={ownership.ownerConfersToNobody ? 'Nobody holds the owner path' : undefined}
        >
          <Text size="sm">{ownership.ownershipNote ?? NOT_MEASURED}</Text>
        </Alert>
      )}
      {ownership.maintainerConfersToNobody && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title="Nobody holds the maintainer path">
          <Text size="sm">
            The maintainer is recorded as a {ownership.maintainerPrincipalType ?? 'principal'} rather than a user, or
            with a blank id. Maintainership matches a single user only, so it confers nothing on anyone.
          </Text>
        </Alert>
      )}
    </Stack>
  )
}

function DirectGrants({ grants, count }: { grants: ReportAccessGrant[]; count: number }) {
  if (grants.length === 0) {
    return <Text size="sm" c="dimmed" mt="xs">No explicit grants exist on this report.</Text>
  }

  return (
    <Stack gap="xs" mt="xs">
      <Table.ScrollContainer minWidth={680}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Principal</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Granted</Table.Th>
              <Table.Th>Expires</Table.Th>
              <Table.Th>Standing</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grants.map((grant) => (
              <Table.Tr key={grant.permissionId} style={grant.confersAccess ? undefined : MUTED}>
                <Table.Td>
                  <Person name={grant.principalDisplayName} id={grant.principalId} />
                  <Text size="xs" c="dimmed">{grant.principalType}</Text>
                  {grant.grantNote && <Text size="xs" fs="italic">&ldquo;{grant.grantNote}&rdquo;</Text>}
                </Table.Td>
                <Table.Td><Text size="sm">{grant.role}</Text></Table.Td>
                <Table.Td>
                  <Text size="xs">{whenUtcLabel(grant.grantedAt)}</Text>
                  <Text size="xs" c="dimmed">by {grant.grantedByDisplayName ?? grant.grantedBy ?? NOT_MEASURED}</Text>
                </Table.Td>
                <Table.Td><Text size="xs">{expiresLabel(grant.expiresAt)}</Text></Table.Td>
                <Table.Td>
                  <GrantStanding
                    isLive={grant.isLive}
                    confersAccess={grant.confersAccess}
                    isEffectivePath={grant.isEffectivePath}
                    notExpandedNote={grant.boundaryNote}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <ShowingCount shown={grants.length} total={count} noun="grants" />
    </Stack>
  )
}

function WorkspaceMembership({
  report, members, count,
}: {
  report: ReportAccessReport
  members: ReportAccessMember[]
  count: number
}) {
  if (report.isPersonalNotFiled) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        Personal (not filed). The report is in no workspace, so no membership confers anything on it — it is
        reachable through ownership, maintainership and explicit grants only.
      </Text>
    )
  }

  if (members.length === 0) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        {report.workspaceName ?? NOT_MEASURED} has no membership rows, not even its owner&rsquo;s, so filing the
        report there reaches nobody.
      </Text>
    )
  }

  return (
    <Stack gap="xs" mt="xs">
      <Text size="sm">
        <Text span c="dimmed">Workspace: </Text>
        {workspaceLabel(report)}
      </Text>
      <Table.ScrollContainer minWidth={680}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Member</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Granted</Table.Th>
              <Table.Th>Expires</Table.Th>
              <Table.Th>Standing</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {members.map((member) => (
              <Table.Tr key={member.membershipId} style={member.isLive ? undefined : MUTED}>
                <Table.Td>
                  <Person name={member.userDisplayName} id={member.userId} />
                  {member.isWorkspaceOwner && (
                    <Badge size="xs" variant="outline" color="gray" mt={4}>Workspace owner (own membership)</Badge>
                  )}
                  {member.grantNote && <Text size="xs" fs="italic">&ldquo;{member.grantNote}&rdquo;</Text>}
                </Table.Td>
                <Table.Td><Text size="sm">{member.role}</Text></Table.Td>
                <Table.Td>
                  <Text size="xs">{whenUtcLabel(member.grantedAt)}</Text>
                  <Text size="xs" c="dimmed">by {member.grantedByDisplayName ?? member.grantedBy ?? NOT_MEASURED}</Text>
                </Table.Td>
                <Table.Td><Text size="xs">{expiresLabel(member.expiresAt)}</Text></Table.Td>
                <Table.Td>
                  <GrantStanding
                    isLive={member.isLive}
                    confersAccess={member.isLive}
                    isEffectivePath={member.isEffectivePath}
                    notExpandedNote={null}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <ShowingCount shown={members.length} total={count} noun="memberships" />
    </Stack>
  )
}

function ResponsibilityHistory({ rows, count }: { rows: ReportAccessResponsibility[]; count: number }) {
  if (rows.length === 0) {
    return <Text size="sm" c="dimmed" mt="xs">No transfer is recorded for this report.</Text>
  }

  return (
    <Stack gap="xs" mt="xs">
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>When</Table.Th>
              <Table.Th>Responsibility</Table.Th>
              <Table.Th>From → to</Table.Th>
              <Table.Th>By</Table.Th>
              <Table.Th>Reason</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, index) => (
              <Table.Tr key={`${row.changedAt}-${row.responsibilityType}-${index}`}>
                <Table.Td><Text size="xs">{whenUtcLabel(row.changedAt)}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm">{row.responsibilityType === 'BusinessOwner' ? 'Business owner' : row.responsibilityType}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">
                    {row.previousPrincipalId ?? 'initial assignment'} → {row.newPrincipalId}
                  </Text>
                  {row.newPrincipalType !== 'User' && <Text size="xs" c="dimmed">to a {row.newPrincipalType}</Text>}
                </Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{row.changedBy ?? NOT_MEASURED}</Text></Table.Td>
                <Table.Td><Text size="xs">{row.reason ?? NOT_MEASURED}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <ShowingCount shown={rows.length} total={count} noun="transfers" />
    </Stack>
  )
}

/**
 * Where a grant or membership stands. Non-conferring rows are labelled, not only
 * greyed, so the difference survives a screenshot and a screen reader.
 */
function GrantStanding({
  isLive, confersAccess, isEffectivePath, notExpandedNote,
}: {
  isLive: boolean
  confersAccess: boolean
  isEffectivePath: boolean
  notExpandedNote: string | null
}) {
  if (!isLive) {
    return <Badge size="sm" variant="outline" color="gray">Expired — confers nothing</Badge>
  }

  if (!confersAccess) {
    return (
      <>
        <Badge size="sm" variant="outline" color="gray">Not expanded — confers nothing</Badge>
        {notExpandedNote && <Text size="xs" c="dimmed" mt={4}>{notExpandedNote}</Text>}
      </>
    )
  }

  return isEffectivePath
    ? <Text size="xs">Decides this person&rsquo;s role</Text>
    : <Text size="xs" c="dimmed">Live, outranked by another path</Text>
}

function Person({ name, id }: { name: string | null; id: string }) {
  return (
    <>
      <Text size="sm">{name ?? NOT_MEASURED}</Text>
      <Text size="xs" c="dimmed" ff="monospace" style={{ overflowWrap: 'anywhere' }}>{id}</Text>
    </>
  )
}

function ShowingCount({ shown, total, noun }: { shown: number; total: number; noun: string }): ReactNode {
  if (total <= shown) return null
  return (
    <Text size="xs" c="dimmed">
      Showing {exactNumber(shown)} of {exactNumber(total)} {noun}.
    </Text>
  )
}

const MUTED: CSSProperties = { opacity: 0.6 }

const PATH_LABEL: Record<ReportAccessPath, string> = {
  Owner: 'Business owner',
  CreatorFallbackOwner: 'Owner (creator fallback)',
  Maintainer: 'Maintainer',
  DirectGrant: 'Direct grant',
  WorkspaceMembership: 'Workspace membership',
  None: 'No live path',
}

/** Null on a report is the personal case, not a gap. */
function workspaceLabel(report: ReportAccessReport): string {
  if (report.isPersonalNotFiled) return 'Personal (not filed)'
  const name = report.workspaceName ?? NOT_MEASURED
  return report.isPersonalWorkspace ? `${name} (a personal workspace)` : name
}

/**
 * Null expiry is a recorded fact — the row does not expire — not an unmeasured
 * value, so it says so rather than printing a dash.
 */
function expiresLabel(iso: string | null): string {
  // Every instant in this drawer is a ReportBuilder DateTime served with no offset: read as UTC, not local.
  return iso == null ? 'Does not expire' : whenUtcLabel(iso)
}
